import {
    AlertTriangle,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    Loader2,
    type LucideIcon,
    PanelRightOpen,
    Wrench,
} from 'lucide-react';
import { createContext, type ReactNode, useContext } from 'react';

import { isRecord } from '@apps/web/components/phoebe_chat/record_utils';
import {
    type ToolApprovalBadge,
    type ToolExecutionShellStatus,
} from '@apps/web/components/phoebe_chat_view/tool_calls/types';
import {
    isUuidOnlyValue,
    redactUuidsInText,
} from '@apps/web/components/phoebe_chat_view/tool_calls/uuid_redaction';
import { Badge } from '@apps/web/components/ui/badge';
import { Markdown } from '@apps/web/components/ui/markdown';
import {
    type StatusIntent,
    statusIntentBadgeClassName,
} from '@apps/web/components/ui/status_tokens';
import { formatPhoneNumber } from '@apps/web/lib/contact_utils';
import { cn } from '@apps/web/lib/utils';

const GENERIC_TOOL_FIELD_LIMIT = 8;
const GENERIC_TOOL_LIST_LIMIT = 4;
const GENERIC_TOOL_RESULT_FIELD_LIMIT = 5;
const GENERIC_TOOL_RESULT_LIST_LIMIT = 4;
const GENERIC_TOOL_RESULT_TITLE_KEYS = [
    'name',
    'title',
    'label',
    'display_name',
    'status',
    'id',
];
const GENERIC_TOOL_PRIMARY_TEXT_KEYS = [
    'summary',
    'message',
    'description',
    'status',
];

export const TOOL_SURFACE_HOVER_CLASS =
    'transition-[transform,border-color,background-color,box-shadow] duration-150 ease-out hover:-translate-y-0.5 hover:border-blue-200/70 hover:bg-blue-50/30 hover:shadow-sm hover:shadow-blue-950/5 dark:hover:border-blue-800/60 dark:hover:bg-blue-950/20';

export function safeStringify(value: unknown): string {
    if (typeof value === 'string') {
        return value;
    }
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
}

export function getStringArray(value: unknown): Array<string> {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(
        (item): item is string =>
            typeof item === 'string' && item.trim().length > 0,
    );
}

export function getOptionalString(value: unknown): string | null {
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

export function getRecordArray(value: unknown): Array<Record<string, unknown>> {
    return Array.isArray(value) ? value.filter(isRecord) : [];
}

export function getFirstString(
    ...values: ReadonlyArray<unknown>
): string | null {
    for (const value of values) {
        const result = getOptionalString(value);
        if (result) {
            return result;
        }
    }
    return null;
}

export function formatToolFieldLabel(key: string): string {
    const normalizedKey = key
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/\bmarkdown\b/gi, '')
        .replace(/\s+/g, ' ')
        .trim();
    if (normalizedKey.length === 0) {
        return 'Value';
    }
    return normalizedKey.charAt(0).toUpperCase() + normalizedKey.slice(1);
}

// Coordinator surfaces redact record identifiers; admin surfaces opt out,
// because an investigation exists to produce identifiers. Mirrors
// `redact_unresolved_ids=False` in phoebe_event_agent/references.py.
export const IdentifierRedactionContext = createContext(true);

/** Redact free text unless the surrounding surface opted out. */
export function useRedactedText(): (text: string) => string {
    const redact = useContext(IdentifierRedactionContext);
    return (text: string) => (redact ? redactUuidsInText(text) : text);
}

export function IdentifierRedactionProvider({
    redact,
    children,
}: {
    readonly redact: boolean;
    readonly children: ReactNode;
}) {
    return (
        <IdentifierRedactionContext.Provider value={redact}>
            {children}
        </IdentifierRedactionContext.Provider>
    );
}

// The preview, the count badge and the full-result expand all render the
// same array, so they must agree on which entries survive redaction.
function visibleToolListItems(
    value: Array<unknown>,
    redact: boolean,
): Array<unknown> {
    return redact ? value.filter((item) => !isUuidOnlyValue(item)) : value;
}

function summarizeNestedToolValue(value: unknown): string {
    if (Array.isArray(value)) {
        return `${value.length} item${value.length === 1 ? '' : 's'}`;
    }
    if (isRecord(value)) {
        const fieldCount = Object.keys(value).length;
        return `${fieldCount} field${fieldCount === 1 ? '' : 's'}`;
    }
    return safeStringify(value);
}

export function renderToolValue(
    value: unknown,
    depth: number = 0,
    redact: boolean = true,
): ReactNode {
    if (value === null || value === undefined) {
        return (
            <span className="text-muted-foreground text-xs italic">
                Not provided
            </span>
        );
    }

    if (typeof value === 'string') {
        return (
            <span className="text-foreground text-xs break-words whitespace-pre-wrap">
                {redact ? redactUuidsInText(value) : value}
            </span>
        );
    }

    if (typeof value === 'number') {
        return <span className="text-foreground text-xs">{value}</span>;
    }

    if (typeof value === 'boolean') {
        return (
            <span className="text-foreground text-xs">
                {value ? 'Yes' : 'No'}
            </span>
        );
    }

    if (Array.isArray(value)) {
        const items = visibleToolListItems(value, redact);
        if (items.length === 0) {
            return (
                <span className="text-muted-foreground text-xs italic">
                    None
                </span>
            );
        }

        if (depth > 0) {
            return (
                <span className="text-foreground text-xs">
                    {summarizeNestedToolValue(items)}
                </span>
            );
        }

        const visibleItems = items.slice(0, GENERIC_TOOL_LIST_LIMIT);
        return (
            <div className="space-y-1">
                {visibleItems.map((item, index) => (
                    <div
                        key={`tool-list-item-${index}`}
                        className="flex items-start gap-2"
                    >
                        <span className="text-muted-foreground text-xs">
                            {index + 1}.
                        </span>
                        <div className="min-w-0 flex-1">
                            {renderToolValue(item, depth + 1, redact)}
                        </div>
                    </div>
                ))}
                {items.length > GENERIC_TOOL_LIST_LIMIT ? (
                    <p className="text-muted-foreground text-xs">
                        +{items.length - GENERIC_TOOL_LIST_LIMIT} more items
                    </p>
                ) : null}
            </div>
        );
    }

    if (isRecord(value)) {
        const entries = Object.entries(value).filter(
            ([, fieldValue]) =>
                fieldValue !== undefined &&
                !(redact && isUuidOnlyValue(fieldValue)),
        );
        if (entries.length === 0) {
            return (
                <span className="text-muted-foreground text-xs italic">
                    None
                </span>
            );
        }

        if (depth > 0) {
            return (
                <span className="text-foreground text-xs">
                    {/* Count the kept entries, not the raw value: a collapsed
                        record would otherwise promise more fields than
                        expanding it can ever show. */}
                    {`${entries.length} field${entries.length === 1 ? '' : 's'}`}
                </span>
            );
        }

        const visibleEntries = entries.slice(0, GENERIC_TOOL_FIELD_LIMIT);
        return (
            <div className="space-y-1.5">
                {visibleEntries.map(([key, fieldValue]) => (
                    <div key={key} className="flex items-start gap-3">
                        <span className="text-muted-foreground min-w-[5.5rem] text-xs font-medium">
                            {formatToolFieldLabel(key)}
                        </span>
                        <div className="min-w-0 flex-1">
                            {renderToolValue(fieldValue, depth + 1, redact)}
                        </div>
                    </div>
                ))}
                {entries.length > GENERIC_TOOL_FIELD_LIMIT ? (
                    <p className="text-muted-foreground text-xs">
                        +{entries.length - GENERIC_TOOL_FIELD_LIMIT} more fields
                    </p>
                ) : null}
            </div>
        );
    }

    return (
        <span className="text-foreground text-xs">{safeStringify(value)}</span>
    );
}

function hasGenericToolPreviewValue(value: unknown, redact = true): boolean {
    if (value === null || value === undefined) {
        return false;
    }
    // Redacted-away ids are not previewable content: counting them would
    // render a "1 field" card whose only field never appears.
    if (redact && isUuidOnlyValue(value)) {
        return false;
    }
    if (typeof value === 'string') {
        return value.trim().length > 0;
    }
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    if (isRecord(value)) {
        return Object.values(value).some((fieldValue) =>
            hasGenericToolPreviewValue(fieldValue, redact),
        );
    }
    return true;
}

function getGenericToolResultCountLabel(
    value: unknown,
    redact: boolean,
): string | null {
    if (Array.isArray(value)) {
        const items = visibleToolListItems(value, redact);
        return `${items.length} item${items.length === 1 ? '' : 's'}`;
    }
    if (isRecord(value)) {
        const fieldCount = Object.values(value).filter((fieldValue) =>
            hasGenericToolPreviewValue(fieldValue, redact),
        ).length;
        return `${fieldCount} field${fieldCount === 1 ? '' : 's'}`;
    }
    if (typeof value === 'string') {
        return 'Text';
    }
    if (typeof value === 'number') {
        return 'Number';
    }
    if (typeof value === 'boolean') {
        return 'Yes/no';
    }
    return null;
}

function getGenericToolPrimaryTextKey(
    value: Record<string, unknown>,
): string | null {
    for (const key of GENERIC_TOOL_PRIMARY_TEXT_KEYS) {
        if (getOptionalString(value[key])) {
            return key;
        }
    }
    return null;
}

function getGenericToolPrimaryText(
    value: unknown,
    redact: boolean,
): string | null {
    if (!isRecord(value)) {
        return null;
    }
    const primaryTextKey = getGenericToolPrimaryTextKey(value);
    if (!primaryTextKey) {
        return null;
    }
    const primaryText = getOptionalString(value[primaryTextKey]);
    if (primaryText === null) {
        return null;
    }
    return redact ? redactUuidsInText(primaryText) : primaryText;
}

function getGenericToolRecordPreviewEntries(
    value: Record<string, unknown>,
    primaryTextKey: string | null,
    redact: boolean,
): Array<readonly [string, unknown]> {
    return Object.entries(value)
        .filter(
            ([key, fieldValue]) =>
                key !== primaryTextKey &&
                hasGenericToolPreviewValue(fieldValue, redact),
        )
        .slice(0, GENERIC_TOOL_RESULT_FIELD_LIMIT);
}

function getGenericToolListItemTitle(
    item: Record<string, unknown>,
    redact: boolean,
): string {
    for (const key of GENERIC_TOOL_RESULT_TITLE_KEYS) {
        const value = getOptionalString(item[key]);
        // The key list ends in `id`, so an item carrying no human-readable
        // field would otherwise headline its own record UUID. This title is
        // rendered inline rather than through `renderToolValue`, so it needs
        // the check directly.
        if (value && !(redact && isUuidOnlyValue(value))) {
            return redact ? redactUuidsInText(value) : value;
        }
    }
    return 'Item';
}

function renderGenericToolListItem(
    item: unknown,
    index: number,
    redact: boolean,
): ReactNode {
    if (!isRecord(item)) {
        return (
            <div className="flex items-start gap-2">
                <span className="text-muted-foreground text-xs">
                    {index + 1}.
                </span>
                <div className="min-w-0 flex-1">
                    {renderToolValue(item, 1, redact)}
                </div>
            </div>
        );
    }

    const title = getGenericToolListItemTitle(item, redact);
    const previewEntries = Object.entries(item)
        .filter(
            ([key, fieldValue]) =>
                !GENERIC_TOOL_RESULT_TITLE_KEYS.includes(key) &&
                hasGenericToolPreviewValue(fieldValue, redact),
        )
        .slice(0, 3);

    return (
        <div className="border-border/50 bg-background rounded-md border px-2 py-1.5">
            <p className="text-foreground text-xs font-medium">{title}</p>
            {previewEntries.length > 0 ? (
                <div className="mt-1 space-y-1">
                    {previewEntries.map(([key, fieldValue]) => (
                        <div
                            key={key}
                            className="grid grid-cols-[4.75rem_1fr] gap-2"
                        >
                            <span className="text-muted-foreground text-[10px] font-medium">
                                {formatToolFieldLabel(key)}
                            </span>
                            <div className="min-w-0">
                                {renderToolValue(fieldValue, 1)}
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function renderGenericToolResultPreviewInner(
    value: unknown,
    primaryTextKey: string | null,
    redact: boolean,
): ReactNode {
    if (isRecord(value)) {
        const previewEntries = getGenericToolRecordPreviewEntries(
            value,
            primaryTextKey,
            redact,
        );
        if (previewEntries.length === 0) {
            return null;
        }
        const previewableFieldCount = Object.values(value).filter(
            (fieldValue) => hasGenericToolPreviewValue(fieldValue, redact),
        ).length;

        return (
            <ToolTopicSection title="Result summary">
                <div className="space-y-1.5">
                    {previewEntries.map(([key, fieldValue]) => (
                        <div
                            key={key}
                            className="grid grid-cols-[5.5rem_1fr] items-start gap-2"
                        >
                            <span className="text-muted-foreground text-xs font-medium">
                                {formatToolFieldLabel(key)}
                            </span>
                            <div className="min-w-0">
                                {renderToolValue(fieldValue, 1)}
                            </div>
                        </div>
                    ))}
                    {previewableFieldCount > previewEntries.length ? (
                        <p className="text-muted-foreground text-[10px]">
                            Additional fields are available below.
                        </p>
                    ) : null}
                </div>
            </ToolTopicSection>
        );
    }

    if (Array.isArray(value)) {
        const items = visibleToolListItems(value, redact);
        const visibleItems = items.slice(0, GENERIC_TOOL_RESULT_LIST_LIMIT);
        if (visibleItems.length === 0) {
            return null;
        }

        return (
            <ToolTopicSection title="Items">
                <div className="space-y-1.5">
                    {visibleItems.map((item, index) => (
                        <div key={`generic-result-item-${index}`}>
                            {renderGenericToolListItem(item, index, redact)}
                        </div>
                    ))}
                    {items.length > visibleItems.length ? (
                        <p className="text-muted-foreground text-[10px]">
                            +{items.length - visibleItems.length} more items
                        </p>
                    ) : null}
                </div>
            </ToolTopicSection>
        );
    }

    return (
        <ToolTopicSection title="Value">
            {renderToolValue(value)}
        </ToolTopicSection>
    );
}

function shouldShowGenericToolFullResult(value: unknown): boolean {
    return isRecord(value) || Array.isArray(value);
}

export function GenericToolResultCard({
    value,
    emptyText,
    presentation = 'default',
    onInspect,
    inspectLabel = 'Open full result',
}: {
    readonly value: unknown;
    readonly emptyText: string;
    readonly presentation?: ToolCardPresentation;
    readonly onInspect?: () => void;
    readonly inspectLabel?: string;
}) {
    const redact = useContext(IdentifierRedactionContext);
    const hasValue = hasGenericToolPreviewValue(value, redact);
    if (!hasValue) {
        return (
            <p className="text-muted-foreground text-xs italic">{emptyText}</p>
        );
    }

    const countLabel = getGenericToolResultCountLabel(value, redact);
    const primaryTextKey = isRecord(value)
        ? getGenericToolPrimaryTextKey(value)
        : null;
    const primaryText = getGenericToolPrimaryText(value, redact);

    if (presentation === 'minimal') {
        return (
            <div className="space-y-2">
                {primaryText ? (
                    <p className="text-foreground text-xs leading-relaxed">
                        {primaryText}
                        {countLabel ? (
                            <span className="text-muted-foreground/60">
                                {' · '}
                                {countLabel}
                            </span>
                        ) : null}
                    </p>
                ) : null}
                {renderGenericToolResultPreviewInner(
                    value,
                    primaryTextKey,
                    redact,
                )}
                {shouldShowGenericToolFullResult(value) ? (
                    onInspect ? (
                        <button
                            type="button"
                            onClick={onInspect}
                            className="cursor-pointer text-xs font-medium text-emerald-700 transition-colors hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300"
                        >
                            {inspectLabel}
                        </button>
                    ) : (
                        <details>
                            <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs transition-colors">
                                Full result
                            </summary>
                            <div className="border-border/60 mt-1.5 border-l pl-3">
                                {renderToolValue(value, 0, redact)}
                            </div>
                        </details>
                    )
                ) : null}
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <div className="border-border/60 bg-muted/30 rounded-lg border px-3 py-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-foreground text-sm font-semibold">
                        Result
                    </p>
                    {countLabel ? (
                        <ToolToken tone="blue">{countLabel}</ToolToken>
                    ) : null}
                </div>
                {primaryText ? (
                    <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">
                        {primaryText}
                    </p>
                ) : null}
            </div>
            {renderGenericToolResultPreviewInner(value, primaryTextKey, redact)}
            {shouldShowGenericToolFullResult(value) ? (
                <details className="border-border/60 bg-background rounded-md border px-2 py-1.5">
                    <summary className="text-muted-foreground cursor-pointer text-[10px] font-medium">
                        Full result
                    </summary>
                    <div className="mt-2">
                        {renderToolValue(value, 0, redact)}
                    </div>
                </details>
            ) : null}
        </div>
    );
}

export function ToolValueSection({
    title,
    value,
    emptyText,
}: {
    readonly title: string;
    readonly value: unknown;
    readonly emptyText: string;
}) {
    const redact = useContext(IdentifierRedactionContext);
    const hasValue = value !== null && value !== undefined;

    return (
        <div className="space-y-1">
            <p className="text-muted-foreground text-xs font-medium">{title}</p>
            {hasValue ? (
                <div>{renderToolValue(value, 0, redact)}</div>
            ) : (
                <p className="text-muted-foreground text-xs italic">
                    {emptyText}
                </p>
            )}
        </div>
    );
}

const TOOL_TOKEN_TONE_INTENT: Record<
    'blue' | 'green' | 'neutral' | 'red',
    StatusIntent
> = {
    green: 'positive',
    red: 'negative',
    blue: 'info',
    neutral: 'neutral',
};

export function ToolToken({
    children,
    tone = 'neutral',
}: {
    readonly children: ReactNode;
    readonly tone?: 'blue' | 'green' | 'neutral' | 'red';
}) {
    return (
        <span
            className={cn(
                'inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-medium',
                statusIntentBadgeClassName(TOOL_TOKEN_TONE_INTENT[tone]),
            )}
        >
            {children}
        </span>
    );
}

export function ToolTopicSection({
    title,
    children,
    className,
}: {
    readonly title: string;
    readonly children: ReactNode;
    readonly className?: string;
}) {
    return (
        <div
            role="group"
            aria-label={title}
            className={cn(
                'border-border/60 bg-background rounded-md border p-2',
                className,
            )}
        >
            <p className="text-muted-foreground text-[10px] font-medium tracking-wide uppercase">
                {title}
            </p>
            <div className="mt-1">{children}</div>
        </div>
    );
}

const TOOL_LABEL_ACRONYMS = new Set([
    'adl',
    'cna',
    'cpr',
    'hha',
    'lpn',
    'ot',
    'pca',
    'pt',
    'rn',
]);

export function formatCaregiverSignalLabel(value: string): string {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
        return trimmed;
    }

    if (!/[_-]/.test(trimmed) && trimmed !== trimmed.toLowerCase()) {
        return trimmed;
    }

    const normalized = trimmed.replace(/[_-]+/g, ' ');
    if (normalized.length <= 4 && normalized === normalized.toLowerCase()) {
        return normalized.toUpperCase();
    }

    return normalized
        .split(/\s+/)
        .map((part) =>
            TOOL_LABEL_ACRONYMS.has(part.toLowerCase())
                ? part.toUpperCase()
                : part.length > 0
                  ? `${part.charAt(0).toUpperCase()}${part.slice(1)}`
                  : part,
        )
        .join(' ');
}

export function ToolDetailHeader({
    title,
    subtitle,
    badges = [],
}: {
    readonly title: string;
    readonly subtitle?: string | null;
    readonly badges?: ReadonlyArray<{
        readonly label: string;
        readonly tone?: 'blue' | 'green' | 'neutral' | 'red';
    }>;
}) {
    return (
        <div className="border-border/60 bg-muted/30 rounded-lg border px-3 py-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="text-foreground text-sm font-semibold">
                        {title}
                    </p>
                    {subtitle ? (
                        <p className="text-muted-foreground mt-0.5 text-[11px]">
                            {subtitle}
                        </p>
                    ) : null}
                </div>
                {badges.length > 0 ? (
                    <div className="flex flex-wrap justify-end gap-1">
                        {badges.map((badge) => (
                            <ToolToken
                                key={badge.label}
                                tone={badge.tone ?? 'neutral'}
                            >
                                {badge.label}
                            </ToolToken>
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

export function ToolDetailKeyValue({
    label,
    value,
}: {
    readonly label: string;
    readonly value: ReactNode;
}) {
    return (
        <div className="flex gap-3">
            <span className="text-muted-foreground min-w-[5.5rem] flex-shrink-0 text-xs font-medium">
                {label}
            </span>
            <div className="text-foreground min-w-0 flex-1 text-xs">
                {value}
            </div>
        </div>
    );
}

export function DetailChipList({
    values,
    emptyText = 'Not provided',
}: {
    readonly values: ReadonlyArray<string>;
    readonly emptyText?: string;
}) {
    if (values.length === 0) {
        return (
            <p className="text-muted-foreground text-xs italic">{emptyText}</p>
        );
    }

    return (
        <div className="flex flex-wrap gap-1">
            {values.map((value) => (
                <Badge
                    key={value}
                    variant="secondary"
                    className="rounded-full px-1.5 py-0 text-[10px]"
                >
                    {formatCaregiverSignalLabel(value)}
                </Badge>
            ))}
        </div>
    );
}

export function CaregiverToolErrorCard({
    title,
    error,
}: {
    readonly title: string;
    readonly error: string;
}) {
    return <ToolIssueCard title={title} error={error} />;
}

export function formatToolIssueMessage(error: string): string {
    const trimmed = error.trim();
    const normalized = trimmed.toLowerCase();

    if (/invalid .*(?:_id| id)/i.test(trimmed) || normalized.includes('uuid')) {
        return 'Phoebe couldn’t find the record it tried to use. This step needs a quick review, but the rest of the conversation is okay.';
    }

    if (
        normalized.includes('not found') ||
        normalized.includes('could not find') ||
        normalized.includes('cannot find')
    ) {
        return 'Phoebe couldn’t find the record it needed for this step. Review the selected record and try again.';
    }

    if (
        normalized.includes('timed out') ||
        normalized.includes('timeout') ||
        normalized.includes('temporarily unavailable') ||
        normalized.includes('overloaded') ||
        normalized.includes('rate limit') ||
        normalized.includes('network')
    ) {
        return 'This step didn’t finish because a service was temporarily unavailable. Trying again usually fixes it.';
    }

    if (
        normalized.includes('permission') ||
        normalized.includes('unauthorized') ||
        normalized.includes('forbidden') ||
        normalized.includes('access denied')
    ) {
        return 'Phoebe didn’t have access to finish this step. A teammate may need to check permissions.';
    }

    if (normalized.includes('not completed') || normalized.includes('failed')) {
        return 'This step didn’t make a change. Review it before trying again.';
    }

    return trimmed || 'This step needs a quick review.';
}

export function ToolIssueCard({
    title,
    error,
    detailsLabel = 'Details for support',
}: {
    readonly title: string;
    readonly error: string;
    readonly detailsLabel?: string;
}) {
    const message = formatToolIssueMessage(error);
    const showDetails = error.trim().length > 0 && error.trim() !== message;

    return (
        <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2 text-amber-950 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-300">
            <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-700 dark:text-amber-400" />
                <div className="min-w-0">
                    <p className="text-xs font-semibold">{title}</p>
                    <p className="mt-1 text-xs text-amber-900 dark:text-amber-200">
                        {message}
                    </p>
                    {showDetails ? (
                        <details className="mt-2">
                            <summary className="cursor-pointer text-[10px] font-medium text-amber-800 dark:text-amber-300">
                                {detailsLabel}
                            </summary>
                            <p className="mt-1 font-mono text-[10px] break-words whitespace-pre-wrap text-amber-900/90 dark:text-amber-200/90">
                                {error}
                            </p>
                        </details>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export function formatDetailStatus(value: string | null): string | null {
    if (!value) {
        return null;
    }
    const normalized = value.toLowerCase();
    if (normalized === 'all' || normalized === 'any') {
        return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    }
    return formatCaregiverSignalLabel(normalized);
}

export type ToolBadgeTone = 'blue' | 'green' | 'neutral' | 'red';

export function getStatusBadge(
    value: string | null,
    tone: ToolBadgeTone = 'blue',
) {
    const label = formatDetailStatus(value);
    return label ? [{ label, tone }] : [];
}

export function ActionMetricGrid({
    metrics,
}: {
    readonly metrics: ReadonlyArray<{
        readonly label: string;
        readonly value: number | null;
    }>;
}) {
    const presentMetrics = metrics.filter(
        (
            metric,
        ): metric is { readonly label: string; readonly value: number } =>
            metric.value !== null,
    );
    if (presentMetrics.length === 0) {
        return null;
    }

    return (
        <ToolTopicSection title="Outcome">
            <div className="grid grid-cols-3 gap-2">
                {presentMetrics.map((metric) => (
                    <div key={metric.label} className="min-w-0">
                        <p className="text-foreground text-sm font-semibold">
                            {metric.value}
                        </p>
                        <p className="text-muted-foreground text-[10px]">
                            {metric.label}
                        </p>
                    </div>
                ))}
            </div>
        </ToolTopicSection>
    );
}

export function joinPresent(
    parts: ReadonlyArray<string | null>,
): string | null {
    const present = parts.filter(
        (part): part is string => part !== null && part.trim().length > 0,
    );
    return present.length > 0 ? present.join(', ') : null;
}

export function formatContactLine(
    phone: string | null,
    email: string | null,
): string | null {
    return joinPresent([formatPhoneNumber(phone), email]);
}

export function formatTimestamp(value: string, timezone: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: timezone,
    });
}

export function getToolExecutionShellStatus({
    isComplete,
    error,
}: {
    readonly isComplete: boolean;
    readonly error?: string | null;
}): ToolExecutionShellStatus {
    if (error) {
        return 'error';
    }
    return isComplete ? 'completed' : 'running';
}

export function formatToolExecutionDuration(
    durationMs: number | null | undefined,
): string | null {
    if (
        durationMs === null ||
        durationMs === undefined ||
        !Number.isFinite(durationMs) ||
        durationMs < 0
    ) {
        return null;
    }

    if (durationMs < 1000) {
        return `${Math.round(durationMs)} ms`;
    }

    if (durationMs < 10_000) {
        return `${(durationMs / 1000).toFixed(1)} s`;
    }

    return `${Math.round(durationMs / 1000)} s`;
}

export type ToolCardPresentation = 'default' | 'minimal';

function MinimalStatusDot({ tone }: { readonly tone: 'working' | 'error' }) {
    if (tone === 'error') {
        return <span className="size-1.5 shrink-0 rounded-full bg-red-600" />;
    }
    return (
        <span className="relative flex size-1.5 shrink-0">
            <span className="bg-foreground/40 absolute inline-flex h-full w-full animate-ping rounded-full" />
            <span className="bg-foreground/60 relative inline-flex size-1.5 rounded-full" />
        </span>
    );
}

function ToolExecutionStatusBadge({
    status,
    presentation = 'default',
}: {
    readonly status: ToolExecutionShellStatus;
    readonly presentation?: ToolCardPresentation;
}) {
    if (presentation === 'minimal') {
        // Completed is the unexceptional state and renders nothing.
        if (status === 'running') {
            return (
                <span className="text-muted-foreground flex items-center gap-1.5 text-[11px]">
                    <MinimalStatusDot tone="working" />
                    Running
                </span>
            );
        }
        if (status === 'error') {
            return (
                <span className="flex items-center gap-1.5 text-[11px] font-medium text-red-700 dark:text-red-400">
                    <MinimalStatusDot tone="error" />
                    Needs review
                </span>
            );
        }
        return null;
    }
    if (status === 'running') {
        return (
            <Badge
                variant="outline"
                className="rounded-full px-2 py-0 text-[10px]"
            >
                <Loader2 className="h-2.5 w-2.5 animate-spin" />
                Running
            </Badge>
        );
    }

    if (status === 'error') {
        return (
            <Badge
                variant="outline"
                className={cn(
                    'rounded-full px-2 py-0 text-[10px]',
                    statusIntentBadgeClassName('warning'),
                )}
            >
                <AlertTriangle className="h-2.5 w-2.5" />
                Needs review
            </Badge>
        );
    }

    return (
        <Badge variant="success" className="rounded-full px-2 py-0 text-[10px]">
            <CheckCircle2 className="h-2.5 w-2.5" />
            Completed
        </Badge>
    );
}

export function ToolExecutionCardShell({
    title,
    toolName,
    icon: ToolIcon,
    status,
    durationLabel,
    approvalBadge,
    detail,
    isOpen,
    onToggle,
    nested,
    isSelected = false,
    usesInspector = false,
    presentation = 'default',
    fullWidth = false,
    children,
}: {
    readonly title: ReactNode;
    readonly toolName: string;
    readonly icon: LucideIcon | undefined;
    readonly status: ToolExecutionShellStatus;
    readonly durationLabel: string | null;
    readonly approvalBadge: ToolApprovalBadge;
    readonly detail: string | null;
    readonly isOpen: boolean;
    readonly onToggle: () => void;
    readonly nested: boolean;
    readonly isSelected?: boolean;
    readonly usesInspector?: boolean;
    readonly presentation?: ToolCardPresentation;
    // Cards hosting a dynamic widget span the transcript column instead of
    // the compact tool-chip width.
    readonly fullWidth?: boolean;
    readonly children: ReactNode;
}) {
    // A dynamic widget is the visible result, not a detail nested inside a
    // generic tool row. Keep the tool-name attribute for diagnostics while
    // letting the template card own all visible chrome and interaction.
    if (fullWidth) {
        const widget = (
            <div
                data-testid="tool-execution-card"
                data-tool-name={toolName}
                className="w-full max-w-full break-words"
            >
                {children}
            </div>
        );
        return nested ? (
            widget
        ) : (
            <div className="flex w-full max-w-full justify-start">{widget}</div>
        );
    }

    if (presentation === 'minimal') {
        const LeadingIcon = usesInspector
            ? PanelRightOpen
            : isOpen
              ? ChevronDown
              : ChevronRight;
        const card = (
            <div
                data-testid="tool-execution-card"
                data-tool-name={toolName}
                className={cn(
                    'w-full max-w-full rounded-[7px] border break-words shadow-[0_1px_0_rgba(0,36,41,0.04)] transition-[transform,border-color,background-color,box-shadow] duration-150 ease-out',
                    isSelected
                        ? 'border-[#42595E] bg-[#F0F5F5] shadow-[0_1px_6px_rgba(0,36,41,0.13)]'
                        : 'border-[#DBE3E3]/90 bg-[#FFFFFF]/95 hover:-translate-y-px hover:border-[#DBE3E3] hover:bg-[#F0F5F5] hover:shadow-[0_3px_10px_rgba(0,36,41,0.07)]',
                )}
            >
                <button
                    type="button"
                    aria-expanded={usesInspector ? undefined : isOpen}
                    aria-pressed={usesInspector ? isSelected : undefined}
                    aria-label={
                        usesInspector ? `Inspect ${toolName}` : undefined
                    }
                    onClick={onToggle}
                    className="flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-left"
                >
                    <LeadingIcon
                        className={cn(
                            'h-3.5 w-3.5 flex-shrink-0',
                            isSelected ? 'text-[#42595E]' : 'text-[#829494]',
                        )}
                    />
                    <div
                        className={cn(
                            'min-w-0 flex-1 truncate text-[13px] font-medium',
                            isSelected ? 'text-[#002429]' : 'text-[#42595E]',
                        )}
                    >
                        {title}
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-2.5">
                        <ToolExecutionStatusBadge
                            status={status}
                            presentation="minimal"
                        />
                        {approvalBadge ? (
                            <span className="text-muted-foreground text-[11px]">
                                {approvalBadge.label}
                            </span>
                        ) : null}
                        {usesInspector ? (
                            <span
                                className={cn(
                                    'rounded-full px-1.5 py-0.5 text-[11px] leading-none',
                                    isSelected
                                        ? 'bg-[#FFFFFF] text-[#42595E] ring-1 ring-[#DBE3E3]'
                                        : 'text-[#829494]',
                                )}
                            >
                                {isSelected ? 'Selected' : 'Inspect'}
                            </span>
                        ) : null}
                        {durationLabel ? (
                            <span className="text-[11px] text-[#829494] tabular-nums">
                                {durationLabel}
                            </span>
                        ) : null}
                    </div>
                </button>
                {!isOpen && detail ? (
                    <div className="-mt-0.5 px-3 pb-2 pl-9 text-[11px] leading-snug text-[#42595E]">
                        {detail}
                    </div>
                ) : null}
                {isOpen ? (
                    <div className="space-y-3 border-t border-[#DBE3E3]/70 px-3 pt-3 pb-3.5">
                        {children}
                    </div>
                ) : null}
            </div>
        );
        if (nested) {
            return card;
        }
        return (
            <div className="ml-7 flex w-[calc(100%-1.75rem)] max-w-full justify-start">
                {card}
            </div>
        );
    }
    const LeadingIcon = ToolIcon ?? Wrench;
    const card = (
        <div
            data-testid="tool-execution-card"
            data-tool-name={toolName}
            className={cn(
                'border-border/50 hover:border-border bg-background rounded-lg border break-words shadow-sm',
                fullWidth ? 'w-full max-w-full' : 'max-w-md',
                TOOL_SURFACE_HOVER_CLASS,
                status === 'error'
                    ? 'border-amber-200 bg-amber-50/20 dark:border-amber-900 dark:bg-amber-950/20'
                    : undefined,
            )}
        >
            <button
                type="button"
                aria-expanded={isOpen}
                onClick={onToggle}
                className="flex w-full cursor-pointer items-start gap-2 px-3 py-2.5 text-left"
            >
                {isOpen ? (
                    <ChevronDown className="text-muted-foreground mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                ) : (
                    <ChevronRight className="text-muted-foreground mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                )}
                <LeadingIcon
                    className={cn(
                        'mt-0.5 h-3.5 w-3.5 flex-shrink-0',
                        status === 'error'
                            ? 'text-amber-700 dark:text-amber-400'
                            : 'text-muted-foreground',
                    )}
                />
                <div className="min-w-0 flex-1 space-y-1">
                    <div className="text-foreground text-xs font-medium">
                        {title}
                    </div>
                    {detail && !isOpen ? (
                        <div className="text-muted-foreground text-[11px] leading-snug">
                            {detail}
                        </div>
                    ) : null}
                    <div className="flex flex-wrap items-center gap-1.5">
                        <ToolExecutionStatusBadge status={status} />
                        {durationLabel ? (
                            <span className="text-muted-foreground text-[11px]">
                                {durationLabel}
                            </span>
                        ) : null}
                        {approvalBadge ? (
                            <Badge
                                variant={approvalBadge.variant}
                                className="rounded-full px-2 py-0 text-[10px]"
                            >
                                {approvalBadge.label}
                            </Badge>
                        ) : null}
                    </div>
                </div>
            </button>
            {isOpen ? (
                <div className="border-border/50 space-y-3 border-t px-3 pt-2.5 pb-3">
                    {children}
                </div>
            ) : null}
        </div>
    );

    if (nested) {
        return card;
    }

    return <div className="flex justify-start">{card}</div>;
}

export function ToolMarkdownResult({
    children,
}: {
    readonly children: string;
}) {
    return <Markdown className="text-foreground text-xs">{children}</Markdown>;
}
